import React from 'react';
import { AppComponentProps, QuickStartCISetupStepType } from '../../typescriptHelpers';
import Text from 'antd/lib/typography/Text';
import { InfoCircleOutlined, CloudUploadOutlined, WarningFilled } from '@ant-design/icons';
import Space from 'antd/lib/space';
import Tooltip from 'antd/lib/tooltip';
import Select from 'antd/lib/select';
import { globalMessage } from '../../utils/globalMessage';
import { antGrey, runwayColors } from '../styles/globalStyles';
import FormItem from 'antd/lib/form/FormItem';
import Alert from 'antd/lib/alert';
import { useForm, useWatch } from 'antd/lib/form/Form';
import { useGetProvisioningProfiles } from './hooks/useGetProvisioningProfiles';
import { TrackedColoredButton } from './ColoredButton';
import { RcFile } from 'antd/lib/upload';
import { useUploadFormWithFile } from './hooks/useUploadFormWithFile';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../reducers/reducers';
import { selectAppPlatform, selectAppStoreIntegration, selectAppStoreType } from '../../selectors/selectors';
import { setQuickStartCIStepData } from '../../actions/ui';
import { formatNameForCertificateType } from '../../helpers/displayHelpers';
import { dayjs } from '@/extendedDayjs';
import { DatetimeFormat } from '../../timeHelpers';
import * as AnalyticEvents from '../../utils/analytics';
import { appEndpoint } from '../../helpers/apiHelpers';
import { debounce } from 'lodash';
import { AppStoreType } from '../../types';
import { UploadSigningKeyFormFields } from '../appSettings/UploadSigningKeyFormFields';

export const SigningSecretsForm: React.FC<AppComponentProps> = ({ appId, orgId }) => {
    const platform = useSelector((state: RootState) => selectAppPlatform(state, appId));
    const appStoreType = useSelector((state: RootState) => selectAppStoreType(state, appId));
    const isAppleAppStore = appStoreType == AppStoreType.Apple;

    const [uploadResult, uploadIsLoading, uploadError, upload] = useUploadFormWithFile();
    const [validateResult, validateIsLoading, validateError, validate] = useUploadFormWithFile<
        Record<string, string>,
        {
            valid: boolean;
        }
    >();

    const appUrl = appEndpoint(orgId, appId);

    const [rcFile, setRcFile] = React.useState<RcFile>();

    const [form] = useForm();

    const selectedProvisioningProfile = useWatch('provisioningProfile', form);
    const selectedSigningKeyPassword = useWatch('signingKeyPassword', form);

    const [
        provisioningProfilesResult,
        provisioningProfilesLoading,
        provisioningProfilesError,
        getProvisioningProfiles,
    ] = useGetProvisioningProfiles(orgId, appId);

    const appStoreIntegration = useSelector((state: RootState) => selectAppStoreIntegration(state, appId));

    const dispatch = useDispatch();

    React.useEffect(() => {
        if (uploadResult) {
            globalMessage.success('Successfully uploaded signing secrets!');

            if (isAppleAppStore) {
                dispatch(
                    setQuickStartCIStepData({
                        stepType: QuickStartCISetupStepType.UploadSecrets,
                        appId,
                        data: {
                            provisioningProfileId: form.getFieldValue('provisioningProfile'),
                        },
                    }),
                );
            }
        }
    }, [uploadResult]);

    React.useEffect(() => {
        if (provisioningProfilesError) {
            globalMessage.error(provisioningProfilesError.message);
        }
    }, [provisioningProfilesError]);

    React.useEffect(() => {
        if (uploadError) {
            globalMessage.error(uploadError.message);
        }
    }, [uploadError]);

    React.useEffect(() => {
        if (!provisioningProfilesResult && isAppleAppStore && appStoreIntegration) {
            getProvisioningProfiles();
        }
    }, [provisioningProfilesResult, platform, appStoreIntegration]);

    const debouncedValidate = React.useCallback(
        debounce((values) => {
            validate(values);
        }, 1000),
        [],
    );

    React.useEffect(() => {
        if (rcFile && selectedProvisioningProfile) {
            debouncedValidate({
                file: rcFile,
                formValues: {
                    provisioningProfileId: selectedProvisioningProfile,
                    signingKeyPassword: selectedSigningKeyPassword ?? '',
                },
                uploadUrl: `${window.location.origin}/${appUrl}/validateProvisioningProfile`,
            });
        }
    }, [rcFile, selectedProvisioningProfile, selectedSigningKeyPassword]);

    return (
        <UploadSigningKeyFormFields
            onUpload={(values) => {
                const { file, ...rest } = values;
                const formValues = rest as Record<string, string>;

                upload({
                    file,
                    formValues: formValues,
                    uploadUrl: `${window.location.origin}/${appUrl}/signingSecrets/${
                        isAppleAppStore ? 'ios' : 'android'
                    }`,
                });
            }}
            form={form}
            appId={appId}
            orgId={orgId}
            onSetRcFile={(rcFile) => {
                setRcFile(rcFile);
            }}
            extraContent={
                <>
                    {isAppleAppStore && (
                        <FormItem
                            name="provisioningProfile"
                            label={
                                <Space>
                                    <Text style={{ color: antGrey.grey8, fontSize: 16 }}>Provisioning profile</Text>
                                    <Tooltip
                                        title={`The provisioning profile and associated signing certificate to use when building your app. Please note that the signing certificate included in your chosen profile must match the certificate that you upload to GitHub's signing secrets`}
                                    >
                                        <InfoCircleOutlined style={{ color: runwayColors.info }} />
                                    </Tooltip>
                                </Space>
                            }
                        >
                            <Select
                                disabled={provisioningProfilesLoading || !appStoreIntegration || validateIsLoading}
                                loading={provisioningProfilesLoading || validateIsLoading}
                                options={provisioningProfilesResult?.profiles?.map((profile) => ({
                                    label: (
                                        <Space direction="vertical" size="small">
                                            <Text style={{ color: antGrey.grey8, fontSize: 16 }}>{profile.name}</Text>
                                            {profile.certificates &&
                                                profile.certificates.length > 0 &&
                                                profile.certificates[0] && (
                                                    <Text style={{ color: antGrey.grey7, fontSize: 12 }}>
                                                        {profile.certificates[0].name} (
                                                        {formatNameForCertificateType(
                                                            profile.certificates[0].certificateType,
                                                        )}
                                                        ) - {'Expires: '}
                                                        {dayjs(profile.certificates[0].expirationDate).format(
                                                            DatetimeFormat.ShortMonthDateYear,
                                                        )}
                                                    </Text>
                                                )}
                                        </Space>
                                    ),
                                    value: profile.id,
                                }))}
                                placeholder="Select provisioning profile"
                                style={{
                                    width: '100%',
                                }}
                            />
                        </FormItem>
                    )}
                    <Space direction="vertical">
                        {!appStoreIntegration && (
                            <Alert
                                message="To use provisioning profiles, you must first set up your app store integration."
                                type="info"
                                style={{
                                    backgroundColor: antGrey.grey2,
                                    borderColor: antGrey.grey5,
                                }}
                            />
                        )}
                        {validateIsLoading && (
                            <Text style={{ color: antGrey.grey7, fontSize: 12 }}>
                                Validating provisioning profile...
                            </Text>
                        )}
                        {validateError && (
                            <Alert
                                message={`Error validating profile: ${validateError.message}`}
                                type="error"
                                showIcon
                            />
                        )}
                        {validateResult?.valid === true && (
                            <Alert
                                message={`Selected provisioning profile matches selected signing certificate`}
                                type="success"
                                showIcon
                            />
                        )}
                        {validateResult?.valid === false && (
                            <Tooltip
                                title={`Builds cannot be properly signed with the selected provisioning profile and signing certificate pair. Create a new App Store distribution provisioning profile and include the selected signing certificate to enable your workflow to provision and sign builds for distribution.`}
                            >
                                <Alert
                                    showIcon
                                    icon={<WarningFilled />}
                                    message="The selected provisioning profile does not match the selected signing certificate"
                                    type="warning"
                                />
                            </Tooltip>
                        )}
                    </Space>
                    <FormItem
                        style={{
                            marginTop: 20,
                        }}
                    >
                        <TrackedColoredButton
                            eventName={AnalyticEvents.Event.ClickQuickStartCICta}
                            extraProperties={{
                                ctaType: AnalyticEvents.QuickStartCICTAs.UploadRepoSecret,
                            }}
                            type="primary"
                            htmlType="submit"
                            icon={<CloudUploadOutlined />}
                            loading={uploadIsLoading}
                        >
                            Upload to repository secrets
                        </TrackedColoredButton>
                    </FormItem>
                </>
            }
        />
    );
};
